import {isObservableObject,isObservableArray, runInAction, IObservableArray, IObservable} from "mobx";
import { DomainDataValueChangeEvent, FormBean, IDomainData } from "../datatypes";
import {toObjectIdMap, getDomainDataIdentifier, ID_SEPARATOR} from "./props-utils";

/**
 * Returns whether the argument is an array or observable array.
 */
export function isArrayLike(x:any) {
    return Array.isArray(x) || isObservableArray(x)
}

export function emptyMobxObj(obj:any){
  if(isArrayLike(obj)) {
    obj.clear();
    return;
  }
  for (let member in obj) {
    if(member && obj[member] && (isObservableObject(obj[member]) || isArrayLike(obj[member]))){
      emptyMobxObj(obj[member])
    } else {
      delete obj[member];
    }
  }
}

/**
 * Simple object check.
 * @param item
 * @returns {boolean} true if item is object and is not array.
 */
export function isObject(item:any) {
  return (item && typeof item === 'object' && !Array.isArray(item));
}

/**
 * 
 * @param {*} targetObjectMap target object map where key is  domain data identifier
 * @param {*} srcObjectMap source object map where key is domain data identifier
 * @param {*} updateEvents domain data update event list
 * @param {*} formBean 
 */
export function mergeByIds(targetObjectMap:{[key:string]:IDomainData}, 
                           srcObjectMap:{[key:string]:IDomainData}, 
                           updateEvents:DomainDataValueChangeEvent[], 
                           formBean:FormBean) {
  updateEvents.sort((a,b)=> {
      let indexa = a.index == null? -1 : a.index;
      let indexb = b.index == null? -1 : b.index;
      return indexa - indexb;
  });

  runInAction(() => {
    updateEvents.forEach(update =>{
      try {
        let targetObj = targetObjectMap[update.edgeName+ID_SEPARATOR+update.objectId];
        let targetParentObj = update.parentObjectId?  targetObjectMap[update.parentEdgeName+ID_SEPARATOR+update.parentObjectId] : undefined;
        let srcObj = srcObjectMap[update.edgeName+ID_SEPARATOR+update.objectId];

        switch (update.changeType) {
          case 'NEW':
          case 'UPDATE'://update can be handled the same way
            //cases need to be handled
            // new attribute
            // new object
            // new object in array

            if (update.attributeName === 'Id') {
              //the target object will be undefined as it tried to look up object by new Id
              targetObj = targetObjectMap[update.edgeName+ID_SEPARATOR+update.previousValue];
              // srcObj could be brand new one that has been added to targetParentObj 
              // by NEW update event whose attributeName is null.
              // Need to avoid add duplicate child to targetParentObj later on
            }
            
            if (!targetObj) {//the object doesn't exist at all we need to add this to the parent
              if (!targetParentObj) {
                //target parent doesn't exist at all, it must be the root level object
                mergeDiffDeep(formBean.DomainObject[update.objectName], srcObj);
              } else if (isArrayLike(targetParentObj[update.objectName]) || Array.isArray(targetParentObj[update.objectName])) {
                if (!targetParentObj[update.objectName]) {
                  targetParentObj[update.objectName] = []; // useless code
                }
                if(!(targetParentObj[update.objectName] as IDomainData[]).find((x)=>x.Id === srcObj.Id)){
                  // prevent same child being added twice!
                  (targetParentObj[update.objectName] as IDomainData[]).push(srcObj)
                }
                //populate new objects in to target by ids too
                toObjectIdMap(srcObj, targetObjectMap)
              } else {
                if (isObject(targetObj[update.attributeName])) {
                  mergeDiffDeep(targetObj[update.attributeName], srcObj[update.attributeName] as IDomainData);
                } else {
                  targetParentObj[update.objectName] = srcObj;
                }
              }
              if (Array.isArray(srcObj)) {
                srcObj.forEach(o => targetObjectMap[getDomainDataIdentifier(o)] = o)
              }
              targetObjectMap[getDomainDataIdentifier(srcObj)] = srcObj;
            } else if (update.attributeName) {
              if (update.attributeName === 'Id') {
                targetObjectMap[getDomainDataIdentifier(srcObj)] = targetObj;
              }
              if (isObject(targetObj[update.attributeName])) {
                mergeDiffDeep(targetObj[update.attributeName] as IDomainData, srcObj[update.attributeName] as IDomainData);
              } else {
                targetObj[update.attributeName] = srcObj[update.attributeName];
              }
            } else {
              console.error('Unhandled update:' + JSON.stringify(update))
            }

            break;
          case 'DELETE':
            if (update.attributeName) {
              deleteProperty(targetObj, update.attributeName);
            } else if (targetParentObj) {
              // replace isObservableArray with isArrayLike
              //if the entire array is cleared, then we will receive the delete one by one
              if (isArrayLike(targetParentObj[update.objectName])) {
                let removed = (targetParentObj[update.objectName] as IObservableArray<IDomainData>).remove(targetObj);
                if (!removed) {
                  console.warn("didn't find remove object: " + JSON.stringify(update));
                }
              } else {
                deleteProperty(targetParentObj, update.objectName);
              }
            } else if (targetObj) {
              emptyMobxObj(targetObj);
            } else {
              console.warn('Unhandled delete:' + JSON.stringify(update))
            }
            break;
          default:
            console.warn('unhandled update', update)
            break;
        }
      } catch (err) {
        console.log("Error handling update", err, update);
      }
    })
  })
}

function deleteProperty(target:any, propertyName:string){
  if(isArrayLike(target[propertyName])){
    while(target[propertyName].length > 0){
      target[propertyName].pop();
    }
  } else if(isObject(target[propertyName])){
    emptyMobxObj(target[propertyName]);
  } else {
    target[propertyName] = null;
  }
}
/**
 * TODO: why not replace target object directly ???
 * 
 * Deep merge two objects.
 */
export function mergeDiffDeep(target:IDomainData, ...sources:IDomainData[]) {
//   console.debug(`mergeDiffDeep target is ${typeof target} sources is ${typeof sources} sources.length=`+sources.length);
//   console.debug('mergeDiffDeep sources:', sources, 'target', target);    
  // e.g mergeDiffDeep target is object sources is object sources.length=undefined
  runInAction(() => {
    if (!sources.length) return target;
    const source = sources.shift();

    if (isObject(target) && isObject(source)) {
      for (const key in source) {
        if (isObject(source[key])) {
          if (!target[key]) {
            target[key] = source[key];
          }else{
            // deep merge source[key] recursively
            mergeDiffDeep(target[key] as IDomainData, source[key] as IDomainData);
          }
        } else if (Array.isArray(source[key])) {
            // console.debug('mergeDiffDeep array. source:', source[key], 'target', target[key]);
          // TODO: implement merge when the length are not equal.
          // this is very tricky. e.g what if array items are swapped or delete or inserted in the middle, or target[key] is not object !!!
          if (!target[key]) {
            target[key] = source[key];
          } else if ((target[key] as IDomainData[]).length == (source[key] as IDomainData[]).length) {
            for (let idx = 0; (source[key] as IDomainData[]).length > idx; idx++) {
              mergeDiffDeep((target[key] as IDomainData[])[idx], (source[key] as IDomainData[])[idx]);
            }
          } else {
            while((target[key] as IDomainData[]).length > 0) {
              (target[key] as IDomainData[]).pop();
            }
            for (let idx = 0; (source[key] as IDomainData[]).length > idx; idx++) {
              (target[key] as IDomainData[]).push((source[key] as IDomainData[])[idx])
            }
          }
        }
        else {
          if (target[key] != source[key]) {
            if(source[key]=== null && target[key]) {
              if (!isObject(target[key])){
                target[key] = source[key];
              }
              else{
                //We delete only attributes recursively, but keep references to objects as they are in the ctx
                emptyMobxObj(target[key]);
              }
            } else {
              target[key] = source[key];
            }
          }
        }
      }
    }
    if(sources.length>0){
        return mergeDiffDeep(target, ...sources);
    }
  });
}
